import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { GetHelpCenterArticleList } from '@/apis/helpCenterAPI';
import { useToken } from '@/common/helper';
import type { HelpCategoryResponse, HelpCategoryViewProps } from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import HelpCategoryArticleRow from '../HelpCategoryArticleRow/HelpCategoryArticleRow';

const HelpCategoryView = ({
  categoryId,
  onOpenArticle,
}: HelpCategoryViewProps) => {
  const { token } = useToken();
  const [data, setData] = useState<HelpCategoryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ignores a response that lands after the user moved to another topic.
    let cancelled = false;
    setLoading(true);
    GetHelpCenterArticleList(token, categoryId)
      .then((res) => {
        if (!cancelled) setData(res?.data?.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, categoryId]);

  if (loading) {
    return (
      <div className={styles.categoryState}>
        <Spin />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.categoryState}>
        Could not load these articles. Please try again.
      </div>
    );
  }

  return (
    <section className={styles.categoryView}>
      <div className={styles.categoryCount}>
        {data.total} {data.total === 1 ? 'article' : 'articles'}
      </div>

      {data.articles.length === 0 ? (
        <div className={styles.categoryState}>
          No articles in this topic yet.
        </div>
      ) : (
        <div className={styles.categoryList}>
          {data.articles.map((article) => (
            <HelpCategoryArticleRow
              key={article.id}
              article={article}
              onSelect={onOpenArticle}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HelpCategoryView;
